import { Suspense, lazy } from 'react';
import type { ComponentType } from 'react';
import { Spin } from '@timeedit/ui-components/antd';

export const EWidgetType = {
  Evaluation: {
    type: 'Evaluation',
  },
  Links: {
    type: 'Links',
    props: [
      {
        key: 'links',
        type: 'array',
        props: [
          { key: 'text', required: true, type: 'string' },
          { key: 'url', required: true, type: 'string' },
        ],
      },
    ],
  },
  Text: {
    type: 'Text',
    props: [{ type: 'string', key: 'content' }],
  },
  ExamInvigilatorInvitations: {
    type: 'ExamInvigilatorInvitations',
  },
  Availability: {
    type: 'Availability',
  },
  UpcomingExam: {
    type: 'UpcomingExam',
  },
  Registration: {
    type: 'Registration',
  },
  MySchedule: {
    type: 'MySchedule',
  },
} as const;

// Better type safety
type WidgetType = keyof typeof EWidgetType;
type WidgetProps = Record<string, any>;

interface WidgetsProps {
  type: WidgetType;
  props: WidgetProps;
}

// Pre-load widgets to avoid runtime loading
const WidgetComponents: Record<WidgetType, ComponentType<any>> = {
  Evaluation: lazy(() => import('./Evaluation/Evaluation.widget')),
  Links: lazy(() => import('./Links/Links.widget')),
  Text: lazy(() => import('./Text/Text.widget')),
  ExamInvigilatorInvitations: lazy(
    () =>
      import('./ExamInvigilatorInvitations/ExamInvigilatorInvitations.widget')
  ),
  Availability: lazy(() => import('./Availability/Availability.widget')),
  UpcomingExam: lazy(() => import('./UpcomingExam/UpcomingExam.widget')),
  Registration: lazy(() => import('./Registration/Registration.widget')),
  MySchedule: lazy(() => import('./MySchedule/MySchedule.widget')),
};

// Error boundary component
const WidgetErrorFallback = ({ type }: { type: string }) => (
  <div style={{ padding: '16px', textAlign: 'center', color: '#ff4d4f' }}>
    Failed to load widget: {type}
  </div>
);

// Wrapper function with better API
export default function Widgets(type: WidgetType, props: WidgetProps) {
  return <RenderWidget type={type} props={props} />;
}

function RenderWidget({ type, props }: WidgetsProps): JSX.Element {
  const LazyWidget = WidgetComponents[type];

  if (!LazyWidget) {
    return <WidgetErrorFallback type={type} />;
  }

  return (
    <Suspense fallback={<Spin size="small" />}>
      <LazyWidget {...props} />
    </Suspense>
  );
}
